import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, MenuItem } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import GroupButton from 'components/Buttons/ButtonGroup';
import ContainerCard from 'components/Containers/Card/ContainerCard';
import CardWrapper from 'components/Financial/CardWrapper';
import * as Yup from 'yup';
import AutoCompleteContext from 'components/CustomInputsInFormikContext/AutoComplete';
import api from 'services/api';
import { getBigger, getOptionalLabelParent } from './helpers';
import TextFieldContext from 'components/CustomInputsInFormikContext/TextField';
import SelectFieldContext from 'components/CustomInputsInFormikContext/Select';
import { useDependencyInjection } from 'hooks/useDependencyInjection';
import HtmlTootipInfo from 'components/tooltips/HtmlTootipInfo';
import { labelField } from 'utils';

function AccountCenter() {
  const [parents, setParents] = useState([]);
  const [space, setSpace] = useState(0);
  const [openProgress, setOpenProgress] = useState(false);

  const {
    classes,
    history,
    error,
    success,
    loadingRequest,
    requestCompleted,
    id,
    disable,
    location,
  } = useDependencyInjection();

  const formik = useFormik({
    initialValues: {
      description: '',
      type: '',
      analytical: true,
      parent: null,
      cost_type: null,
    },
    validationSchema: Yup.object({
      description: Yup.string().required('Campo Obrigatório'),
      type: Yup.string().required('Campo Obrigatório'),
      cost_type: Yup.string().required('Campo Obrigatório').nullable(),
      analytical: Yup.boolean().required('Campo Obrigatório'),
      parent: Yup.object().required('Campo Obrigatório').nullable(),
    }),
    onSubmit: (values) => {
      request(values);
    },
  });

  useEffect(() => {
    if (location.state !== undefined) {
      if (location.state.customData) {
        setData(location.state.customData);
      } else if (location.state.data) {
        setData(location.state.data);
      }
    }
  }, [location]);

  function setData(data) {
    formik.setValues(data);
  }

  useEffect(() => {
    async function load() {
      try {
        setOpenProgress(true);
        const parentsData = (await api.get('/accounts-center/')).data;

        setParents(parentsData);
        setSpace(getBigger(parentsData));
      } catch (e) {
        console.error(e);
      } finally {
        setOpenProgress(false);
      }
    }
    load();
  }, []);

  async function request(data) {
    try {
      loadingRequest();
      if (id) {
        await api.put(`/accounts-center/${id}`, { ...data });
      } else {
        await api.post('/accounts-center', { ...data });
      }
      success();
      history.push('/accounts-center');
    } catch (err) {
      error();
    } finally {
      requestCompleted();
    }
  }

  const isStandardCategory = useMemo(
    () => ['1', '2'].includes(formik.values.cod),
    [formik.values.cod]
  );

  const isDuplicated = !!location?.state?.customData;

  return (
    <FormikProvider value={formik}>
      <ContainerCard loading={openProgress}>
        <Form noValidate>
          <Box className={classes.root}>
            <CardWrapper
              title="Centro de custo"
              tooltipMessage="A categoria de rateio é usada para dividir suas contas e gerar relatórios detalhados. "
            >
              {id && (
                <Grid item xs={12} sm={4}>
                  <TextFieldContext disabled label="Código" name="cod" />
                </Grid>
              )}
              {!isStandardCategory && !id && (
                <Grid item xs={12} sm={4}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                  >
                    <AutoCompleteContext
                      getOptionLabel={getOptionalLabelParent(space)}
                      noOptionsText="Sem opções"
                      disabled={id}
                      id="parent"
                      name="parent"
                      options={parents}
                      label={labelField('Categoria Pai')}
                      onChangeCallback={(value) => {
                        formik.setFieldValue('type', value?.type);
                      }}
                    />
                    <HtmlTootipInfo message="Selecione uma categoria a qual sua nova categoria irá pertencer. Isso permitirá criar uma árvore de categorias." />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <TextFieldContext
                  disabled={isStandardCategory}
                  label={labelField('Nome da categoria')}
                  name="description"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <SelectFieldContext
                  InputLabelProps={{
                    shrink: id || isDuplicated || undefined,
                  }}
                  label={labelField('Tipo de custo')}
                  name="cost_type"
                >
                  <MenuItem key={1} value="Variado">
                    Variado
                  </MenuItem>
                  <MenuItem key={2} value="Fixo">
                    Fixo
                  </MenuItem>
                </SelectFieldContext>
              </Grid>
            </CardWrapper>
          </Box>
          <GroupButton
            deleteRoute="/accounts-center/"
            routeDelete="/accounts-center"
            returnRoute="/accounts-center"
            id={id}
            disable={disable}
            message="A exclusão pode levar a exclusão de objetos que dependam dela. Tem certeza que deseja excluir?"
          />
        </Form>
      </ContainerCard>
    </FormikProvider>
  );
}

export default AccountCenter;
